import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux'
import numberOfAttendeesIcon from '../../../../assets/icons/numberOfAttendees.svg'
import pricingIcon from '../../../../assets/icons/dollar.svg'
import discountIcon from '../../../../assets/icons/discount.svg'
import { formatNumberWithCommas } from '../../../../helpers/LibertyLake/helpers'
import { config as EnvironmentConfig } from '../../../../config'

const Step4 = () => {
  const { kids, config, familyInfo } = useSelector((state: RootState) => state.libertyLake)

  const pricing = useMemo(() => {
    if (!config) return null

    const { basePricePerWeek, aMBusExtendedCare, pMBusExtendedCare } = config.pricing
    const { multiWeek, preSchool, siblingNumber, occupational, newFamily, returningFamily, earlyPay } = config.discounts
    const preSchoolPct = preSchool['Pre-K, Kindergarten'] || 0.05
    const familyFlatDiscountPerKid = familyInfo.isNewFamily
      ? (newFamily['per Kid'] || 0)
      : (returningFamily['per Kid'] || 0)

    const kidBreakdowns = kids.map((kid) => {
      const weeklyRate = basePricePerWeek + (kid.includeAMBus ? aMBusExtendedCare : 0) + (kid.includePMBus ? pMBusExtendedCare : 0)
      const grossPrice = weeklyRate * kid.weeksTotal
      const multiWeekDiscountAmount = (multiWeek[kid.weeksTotal.toString()] || 0) * kid.weeksTotal
      const priceAfterMultiWeek = grossPrice - multiWeekDiscountAmount
      const priceAfterFamilyFlat = Math.max(0, priceAfterMultiWeek - familyFlatDiscountPerKid)
      const isPreSchool = kid.grade === 'Pre thru K'
      const preSchoolDiscountAmount = isPreSchool ? priceAfterFamilyFlat * preSchoolPct : 0
      const priceAfterPreSchool = priceAfterFamilyFlat - preSchoolDiscountAmount
      return { kid, weeklyRate, grossPrice, multiWeekDiscountAmount, preSchoolDiscountAmount, priceAfterPreSchool }
    })

    const subtotal = kidBreakdowns.reduce((sum, b) => sum + b.grossPrice, 0)
    const totalMultiWeekDiscount = kidBreakdowns.reduce((sum, b) => sum + b.multiWeekDiscountAmount, 0)
    const totalFamilyFlatDiscount = familyFlatDiscountPerKid * kids.length
    const totalPreSchoolDiscount = kidBreakdowns.reduce((sum, b) => sum + b.preSchoolDiscountAmount, 0)

    // Sort by priceAfterPreSchool desc for sibling discount
    const sortedBreakdowns = [...kidBreakdowns].sort((a, b) => b.priceAfterPreSchool - a.priceAfterPreSchool)
    let familyPriceAfterSibling = 0
    let totalSiblingDiscount = 0
    sortedBreakdowns.forEach((b, i) => {
      const sibPct = kids.length < 2 ? 0 : (siblingNumber[(i + 1).toString()] || 0)
      const sibDiscount = b.priceAfterPreSchool * sibPct
      totalSiblingDiscount += sibDiscount
      familyPriceAfterSibling += b.priceAfterPreSchool - sibDiscount
    })

    // Occupational discount
    const occPct = occupational[familyInfo.occupation] || 0
    const occupationalDiscount = familyPriceAfterSibling * occPct
    const priceAfterOccupational = familyPriceAfterSibling - occupationalDiscount

    // Early pay discount
    const earlyPayAmountPerWeek = familyInfo.earlyPayOption
      ? (earlyPay[familyInfo.earlyPayOption] || 0)
      : 0
    const totalCamperWeeks = kids.reduce((sum, kid) => sum + kid.weeksTotal, 0)
    const earlyPayDiscount = earlyPayAmountPerWeek * totalCamperWeeks
    const grandTotal = Math.max(0, priceAfterOccupational - earlyPayDiscount)
    const totalDiscount = subtotal - grandTotal

    return {
      subtotal,
      grandTotal,
      totalDiscount,
      totalMultiWeekDiscount,
      totalFamilyFlatDiscount,
      totalPreSchoolDiscount,
      totalSiblingDiscount,
      occupationalDiscount,
      earlyPayDiscount,
      kidBreakdowns
    }
  }, [kids, config, familyInfo])

  if (!pricing) return <div className='step4'>Loading...</div>

  return <>
    <div className='step4'>
      <div className="summaryDetailsArea">
        <h3 className="summaryTitle">Summary</h3>
        <div className="familyInfoCard">
          <h3 className="cardTitle">Guardian's Information</h3>
          <div className="row">
            <label className='fieldLabel'><span>#</span>Name: {familyInfo.firstName} {familyInfo.lastName}</label>
          </div>
          <div className="row">
            <label className='fieldLabel'><span>#</span>Email: {familyInfo.email}</label>
          </div>
          {familyInfo.phone && (
            <div className="row">
              <label className='fieldLabel'><span>#</span>Phone: {familyInfo.phone}</label>
            </div>
          )}
          <div className="row">
            <label className='fieldLabel'><span>#</span>City: {familyInfo.city}</label>
          </div>
          {EnvironmentConfig.env === 'development' && pricing.totalDiscount > 0 && (
            <>
              <div className="row">
                <label className='fieldLabel'><span>#</span>Discounts:</label>
              </div>
              {pricing.totalMultiWeekDiscount > 0 && (
                <div className='row subRow'>
                  <label className='fieldLabel'><span>-</span>Multi-Week Discount (-${formatNumberWithCommas(pricing.totalMultiWeekDiscount)})</label>
                </div>
              )}
              {pricing.totalPreSchoolDiscount > 0 && (
                <div className='row subRow'>
                  <label className='fieldLabel'><span>-</span>Pre-School Discount (-${formatNumberWithCommas(pricing.totalPreSchoolDiscount)})</label>
                </div>
              )}
              {pricing.totalSiblingDiscount > 0 && (
                <div className='row subRow'>
                  <label className='fieldLabel'><span>-</span>Sibling Discount (-${formatNumberWithCommas(pricing.totalSiblingDiscount)})</label>
                </div>
              )}
              {pricing.totalFamilyFlatDiscount > 0 && (
                <div className='row subRow'>
                  <label className='fieldLabel'><span>-</span>{familyInfo.isNewFamily ? 'New' : 'Returning'} Family Discount (-${formatNumberWithCommas(pricing.totalFamilyFlatDiscount)})</label>
                </div>
              )}
              {pricing.occupationalDiscount > 0 && (
                <div className='row subRow'>
                  <label className='fieldLabel'><span>-</span>{familyInfo.occupation} Discount (-${formatNumberWithCommas(pricing.occupationalDiscount)})</label>
                </div>
              )}
              {pricing.earlyPayDiscount > 0 && (
                <div className='row subRow'>
                  <label className='fieldLabel'><span>-</span>Early Pay Discount (-${formatNumberWithCommas(pricing.earlyPayDiscount)})</label>
                </div>
              )}
            </>
          )}
        </div>
        <div className="numberOfAttendees">
          <img src={numberOfAttendeesIcon} className="icon" />
          <label className='fieldLabel'>{kids.length} Attendee(s)</label>
        </div>
        <div className="sessionsDetailsArea">
          {pricing.kidBreakdowns.map(({ kid, weeklyRate, grossPrice, multiWeekDiscountAmount }) => (
            <div className='attendeeDetailCard' key={kid.id}>
              <h3 className='cardTitle'>{kid.id}</h3>
              <div className="mainRow">
                <div className="column1">
                  <div className="row">
                    <label className='fieldLabel'><span>#</span>Grade: {kid.grade}</label>
                  </div>
                  <div className="row">
                    <label className='fieldLabel'><span>#</span>Weeks: {kid.weeksTotal}</label>
                  </div>
                  {kid.includeAMBus && (
                    <div className="row">
                      <label className='fieldLabel'><span>#</span>AM Bus / Extended Care</label>
                    </div>
                  )}
                  {kid.includePMBus && (
                    <div className="row">
                      <label className='fieldLabel'><span>#</span>PM Bus / Extended Care</label>
                    </div>
                  )}
                </div>
                {EnvironmentConfig.env === 'development' && (
                  <div className="column2">
                    <div className="row">
                      <label className='fieldLabel'>${weeklyRate}/week × {kid.weeksTotal} week{kid.weeksTotal > 1 ? 's' : ''} = ${formatNumberWithCommas(grossPrice)}</label>
                    </div>
                    {multiWeekDiscountAmount > 0 && (
                      <div className="row">
                        <label className='fieldLabel'>Multi-week discount: -${formatNumberWithCommas(multiWeekDiscountAmount)}</label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="summaryAmountArea">
        <h3 className="pricingBreakDownTitle">Pricing Breakdown</h3>
        <div className='pricingColumn'>
          <div className="pricingRow">
            <label className='fieldLabel'>Sub Total</label>
            <div className="price">
              <img src={pricingIcon} className='pricingIcon' />
              <h3 className='subPrice'>${formatNumberWithCommas(pricing.subtotal)}</h3>
            </div>
          </div>
          <div className="discountRow">
            <label className='fieldLabel'>Discounts</label>
            <div className="price">
              <img src={discountIcon} className='discountIcon' />
              <h3 className='subPrice'>- ${formatNumberWithCommas(pricing.totalDiscount)}</h3>
            </div>
          </div>
        </div>
        <div className="grandTotal">
          <h3 className='totalText'>Grand Total</h3>
          <h2 className="grandTotalPrice">${formatNumberWithCommas(pricing.grandTotal)}</h2>
        </div>
      </div>
    </div>
  </>
}

export default Step4
